import ApartmentIcon from "@mui/icons-material/Apartment";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import NoteIcon from "@mui/icons-material/Note";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import ReusableTable from "../../common/table/CommonTable";
import ReusableFormFields from "../../common/textfield/ReusableTextfield";
import {
  fetchAdmission,
  fetchAdmissions,
  fetchAllAdmission,
  resetCurrentAdmission
} from "../../redux/admissions/admissionsActions";
import { fetchBlocks } from "../../redux/block/blockAction";
import { fetchDepartments } from "../../redux/department/departmentAction";
import { fetchAllFloors, fetchFloors } from "../../redux/floor/floorAction";
import {
  addTransferPatient,
  resetCurrentTransferPatient,
} from "../../redux/TransferPatient/transferPatientAction";
import { fetchAllWards, fetchWards } from "../../redux/ward/wardAction";

const PatientTransferForm = () => {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  
  // Main form for single patient transfers
  const {
    register,
    watch,
    setValue,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      new_block_id: "",
      new_floor_id: "",
      new_ward_id: "",
      admission_id: "",
      reason: ""
    }
  });

  // Separate form for bulk ward changes
  const {
    register: wardRegister,
    watch: wardWatch,
    setValue: wardSetValue,
    control: wardControl,
    reset: wardReset,
    formState: { errors: wardErrors },
  } = useForm({
    defaultValues: {
      new_block_id: "",
      new_floor_id: "",
      new_ward_id: "",
      reason: ""
    }
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState({
    title: "",
    message: "",
    type: "info"
  });

  const admissionsList = useSelector(
    (state) => state?.admission?.fetchAdmissionsAll || {}
  );

  const updateAdmissionState = useSelector(
    (state) => state?.admission?.updateAdmission || {}
  );
  
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [wardChangeDialogOpen, setWardChangeDialogOpen] = useState(false);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const totalPages = admissionsList?.admissions?.totalPages || 0;
  const [rowsPerPage] = useState(10);
  const searchTimeoutRef = useRef(null);
  const blockList = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorList = useSelector((state) => state?.floor?.fetchFloors || {});
  const wardList = useSelector((state) => state?.ward?.fetchWards || {});
  const loginValues = useSelector((state) => state.values?.loginValues || []);
  const userInfo = loginValues[0]?.user || {};
  const Id = userInfo.staff_id;
  const transferPatientState = useSelector(
    (state) => state?.transferPatient?.addTransferPatients || {}
  );

  const watchBlock = watch("new_block_id");
  const watchFloor = watch("new_floor_id");
  const watchAdmission = watch("admission_id");

  const wardWatchBlock = wardWatch("new_block_id");
  const wardWatchFloor = wardWatch("new_floor_id");

  useEffect(() => {
    if (wardWatchBlock) {
      dispatch(fetchFloors(wardWatchBlock));
      wardSetValue("new_floor_id", "");
      wardSetValue("new_ward_id", "");
    }
  }, [wardWatchBlock, dispatch, wardSetValue]);

  useEffect(() => {
    if (wardWatchBlock && wardWatchFloor) {
      dispatch(fetchWards(wardWatchBlock, wardWatchFloor));
      wardSetValue("new_ward_id", "");
    }
  }, [wardWatchBlock, wardWatchFloor, dispatch, wardSetValue]);

  useEffect(() => {
    if (watchAdmission) {
      dispatch(fetchAdmission(watchAdmission));
    }
  }, [watchAdmission, dispatch]);

  useEffect(() => {
    if (watchBlock) {
      dispatch(fetchFloors(watchBlock));
      setValue("new_floor_id", "");
      setValue("new_ward_id", "");
    }
  }, [watchBlock, dispatch, setValue]);

  useEffect(() => {
    if (watchBlock && watchFloor) {
      dispatch(fetchWards(watchBlock, watchFloor));
    }
  }, [watchBlock, watchFloor, dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchAllAdmission());
      await dispatch(fetchDepartments());
      await dispatch(fetchBlocks());
      await dispatch(fetchAllWards());
      await dispatch(fetchAllFloors());
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    if (transferPatientState.success) {
      setDialogProps({
        title: "Success",
        message: "Patient transfer request has been successfully submitted.",
        type: "success",
      });
      setDialogOpen(true);
      dispatch(resetCurrentTransferPatient());
      fetchData(currentPage);
    } else if (transferPatientState.error) {
      setDialogProps({
        title: "Error",
        message: transferPatientState.error?.message || "Failed to submit patient transfer request. Please try again.",
        type: "error",
      });
      setDialogOpen(true);
      dispatch(resetCurrentTransferPatient());
    }
  }, [transferPatientState, dispatch, currentPage]);



  const handleCloseDialog = () => {
    setDialogOpen(false);
    if (dialogProps.type === "success") {
      reset();
    }
  };

  const getBlockOptions = () => {
    return (
      blockList?.Blocks?.data
        ?.filter((block) => block.block_status !== 2)
        .map((block) => ({
          value: block.id,
          label: block.block_name,
        })) || []
    );
  };

  const getFloorOptions = (blockId = null) => {
  
    if (blockId && floorList?.floors?.data) {
      return floorList.floors.data
        .filter((floor) => floor.floor_status !== 0)
        .map((floor) => ({
          value: floor.id,
          label: floor.floor_name,
        }));
    }
    return (
      floorList?.floors?.data
        ?.filter((floor) => floor.floor_status !== 0)
        .map((floor) => ({
          value: floor.id,
          label: floor.floor_name,
        })) || []
    );
  };

  const getWardOptions = () => {
    if (wardList?.wards?.data) {
      return wardList.wards.data
        .filter((ward) => ward.ward_status !== 0)
        .map((ward) => ({
          value: ward.id,
          label: ward.ward_name,
        }));
    }
    return [];
  };

  const wardChangeFields = [
    {
      name: "new_block_id",
      label: "New Block",
      type: "select",
      options: getBlockOptions(),
      rules: { required: "New block is required" },
      startIcon: <LocalHospitalIcon />,
    },
    {
      name: "new_floor_id",
      label: "New Floor",
      type: "select",
      options: getFloorOptions(wardWatchBlock),
      rules: { required: "Floor selection is required" },
      startIcon: <ApartmentIcon />,
      disabled: !wardWatchBlock,
    },
    {
      name: "new_ward_id",
      label: "New Ward",
      type: "select",
      options: getWardOptions(),
      rules: { required: "Ward type is required" },
      startIcon: <MeetingRoomIcon />,
      disabled: !wardWatchFloor || !wardWatchBlock,
    },
    {
      name: "reason",
      label: "Reason for Transfer",
      type: "text",
      multipleLine: true,
      rules: { required: "Reason for transfer is required" },
      startIcon: <NoteIcon />,
    },
  ];

  const fetchData = async (page) => {
    setLoading(true);
    const payload = {
      pageNo: page,
      pagesize: rowsPerPage,
      pagination_required: true,
      search: searchTerm,
    };

    await dispatch(fetchAdmissions(payload));
    setLoading(false);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, searchTerm, dispatch]);

  useEffect(() => {
    if (
      admissionsList.admissions?.data &&
      Array.isArray(admissionsList.admissions.data)
    ) {
      const formattedData = admissionsList.admissions.data.map((admission) => ({
        id: admission.id,
        admissionNo: admission.admission_no,
        patientId: admission.Patient?.id,
        patientSerialNo: admission.Patient?.patient_serial_no,
        patientName: admission.Patient?.patient_name,
        ipNumber: admission.Patient?.ip_number,
        phone: admission.Patient?.phone,
        admitDate: admission.admit_date,
        dischargeDate: admission.discharge_date,
        blockName: admission.Block?.block_name || "N/A",
        floorName: admission.Floor?.floor_name || "N/A",
        departmentName: admission.Department?.department_name || "N/A",
        wardName: admission.Ward?.ward_name || "N/A",
        status: getStatusLabel(admission.admission_status),
        statusCode: admission.admission_status,
        numAttenders: admission.number_attenders || 0,
        attenders: admission.Attenders || [],
      }));

      setData(formattedData);

      const lowercasedSearchTerm = searchTerm?.toLowerCase() || "";
      const filtered = searchTerm
        ? formattedData.filter(
            (admission) =>
              admission.patientName
                .toLowerCase()
                .includes(lowercasedSearchTerm) ||
              admission.admissionNo
                .toLowerCase()
                .includes(lowercasedSearchTerm) ||
              admission.patientSerialNo
                .toLowerCase()
                .includes(lowercasedSearchTerm) ||
              admission.blockName
                .toLowerCase()
                .includes(lowercasedSearchTerm) ||
              admission.floorName
                .toLowerCase()
                .includes(lowercasedSearchTerm) ||
              admission.wardName.toLowerCase().includes(lowercasedSearchTerm) ||
              admission.departmentName
                .toLowerCase()
                .includes(lowercasedSearchTerm)
          )
        : formattedData;

      setFilteredData(filtered);
    } else {
      setData([]);
      setFilteredData([]);
    }
  }, [admissionsList, searchTerm]);

  useEffect(() => {
    if (updateAdmissionState.success) {
      setSuccessSnackbarOpen(true);
      dispatch(resetCurrentAdmission());
      fetchData(currentPage);
    }
  }, [updateAdmissionState.success, dispatch, currentPage]);

  const getStatusLabel = (statusCode) => {
    switch (statusCode) {
      case 1:
        return "Active";
      case 2:
        return "In Active";
      case 3:
        return "Discharged";
      case 4:
        return "Death";
      case 5:
        return "Abscond";
      case 6:
        return "Referral";
      default:
        return "Unknown";
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchInputValue(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 1000);
  };

  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    if (isChecked) {
      setSelectedPatients(filteredData.map(patient => patient.id));
    } else {
      setSelectedPatients([]);
    }
  };

  const handleSelectPatient = (patientId) => {
    setSelectedPatients(prev => {
      const isSelected = prev.includes(patientId);
      if (isSelected) {
        const newSelection = prev.filter(id => id !== patientId);
        setSelectAll(false);
        return newSelection;
      } else {
        const newSelection = [...prev, patientId];
        setSelectAll(newSelection.length === filteredData.length);
        return newSelection;
      }
    });
  };

  const handleWardChangeOpen = () => {
    if (selectedPatients.length === 0) {
      setDialogProps({
        title: "Warning",
        message: "Please select at least one patient to transfer.",
        type: "warning",
      });
      setDialogOpen(true);
      return;
    }
    setWardChangeDialogOpen(true);
  };

  const handleWardChangeClose = () => {
    setWardChangeDialogOpen(false);
    wardReset();
  };

  const handleWardChangeSubmit = () => {
    const formData = wardWatch();
    if (!formData.new_block_id || !formData.new_ward_id || 
        !formData.new_floor_id || !formData.reason) {
      setDialogProps({
        title: "Validation Error",
        message: "Please fill in all required fields (Block, Floor, Ward, and Reason).",
        type: "error",
      });
      setDialogOpen(true);
      return;
    }

    const payload = {
      admission_ids: selectedPatients,
      new_block_id: formData.new_block_id,
      new_ward_id: formData.new_ward_id,
      new_floor_id: formData.new_floor_id,
      reason: formData.reason,
      staff_id: Id 
    };

    console.log("Ward Change Payload:", payload);
    dispatch(addTransferPatient(payload));
    setWardChangeDialogOpen(false);
    setSelectedPatients([]);
    setSelectAll(false);
    wardReset();
  };

  const handleCloseSuccessSnackbar = () => {
    setSuccessSnackbarOpen(false);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const columns = [
    {
      id: "checkbox",
      label: (
        <Checkbox
          checked={selectAll}
          onChange={handleSelectAll}
          indeterminate={selectedPatients.length > 0 && selectedPatients.length < filteredData.length}
          sx={{
            color: '#1976d2',
            '&.Mui-checked': {
              color: '#1976d2',
            },
            '&.MuiCheckbox-indeterminate': {
              color: '#1976d2',
            },
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            },
          }}
        />
      ),
      sortable: false,
      render: (value, row) => (
        <Checkbox
          checked={selectedPatients.includes(row.id)}
          onChange={() => handleSelectPatient(row.id)}
          sx={{
            color: '#1976d2',
            '&.Mui-checked': {
              color: '#1976d2',
            },
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            },
          }}
        />
      ),
    },
    { id: "admissionNo", label: "Admission No", sortable: true },
    { id: "patientSerialNo", label: "Patient ID", sortable: true },
    { id: "patientName", label: "Patient Name", sortable: true },
    { id: "ipNumber", label: "IP Number", sortable: true },
    { id: "phone", label: "Phone", sortable: true },
    {
      id: "admitDate",
      label: "Admit Date",
      sortable: true,
      render: (value) => {
        const date = new Date(value);
        return isNaN(date) ? "Invalid Date" : date.toLocaleDateString();
      },
    },
    {
      id: "dischargeDate",
      label: "Discharge Date",
      sortable: true,
      render: (value) => {
        if (!value) return "Not Discharged";
        const date = new Date(value);
        return isNaN(date) ? "Invalid Date" : date.toLocaleDateString();
      },
    },
    { id: "blockName", label: "Block", sortable: true },
    { id: "floorName", label: "Floor", sortable: true },
    { id: "departmentName", label: "Department", sortable: true },
    { id: "wardName", label: "Ward", sortable: true },
    { id: "numAttenders", label: "Attenders", sortable: true },
    {
      id: "status",
      label: "Status",
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color={
            value === "Active"
              ? "success"
              : value === "In Active"
              ? "default"
              : value === "Discharged"
              ? "primary"
              : "warning"
          }
        />
      ),
    },
  ];

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Search for patients by name, admission number, or ID
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="search by Patient Name"
                    value={searchInputValue}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4} >
                  <Button
                    variant="contained"
                    onClick={handleWardChangeOpen}
                    disabled={selectedPatients.length === 0}
                    fullWidth
                    sx={{ 
                      height: '40px',
                      background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                      color: 'white',
                      fontWeight: 600,
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 16px rgba(33, 150, 243, 0.4)',
                      },
                      '&:disabled': {
                        background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                        color: '#9e9e9e',
                        transform: 'none',
                        boxShadow: 'none',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    Ward Change ({selectedPatients.length} selected)
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <ReusableTable
        title="Patient list"
        columns={columns}
        data={filteredData}
        isLoading={loading}
        headerColor="#000"
        rowColor="#ffffff"
        rowsPerPage={rowsPerPage}
        alternateRowColor="#f5f5f5"
        withPagination={true}
        withActions={false}
        showDownloadOption={true}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        emptyMessage="No records found"
      />

      <Dialog
        open={wardChangeDialogOpen}
        onClose={handleWardChangeClose}
        aria-labelledby="ward-change-dialog-title"
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle
          id="ward-change-dialog-title"
          sx={{ 
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: "white",
            fontWeight: 600,
            py: 2
          }}
        >
          Ward Change - {selectedPatients.length} Patient(s) Selected
        </DialogTitle>
        <DialogContent sx={{ mt: 5 }}>
          <Grid container spacing={2} marginTop={4}>
            <ReusableFormFields
              inputFields={wardChangeFields}
              register={wardRegister}
              errors={wardErrors}
              setValue={wardSetValue}
              control={wardControl}
              gridProps={{ xs: 12 }}
            />
            
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: '#fafafa' }}>
          <Button
            onClick={handleWardChangeClose}
            sx={{
              backgroundColor: "#ff9800",
              color: "white",
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "#f57c00",
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(255,152,0,0.3)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleWardChangeSubmit}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              color: "white",
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              "&:hover": {
                background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(76,175,80,0.3)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Submit Ward Change
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSuccessSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSuccessSnackbar}
          severity="success"
          sx={{ width: '100%' }}
        >
          Operation completed successfully!
        </Alert>
      </Snackbar>

      {/* Dialog for success/error messages */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>{dialogProps.title}</DialogTitle>
        <DialogContent>
          <Typography>{dialogProps.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="green" backgroundColor="#4caf50" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientTransferForm;